import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ROUTES } from './config/routes.config';
import {
  PORTAL_PERMISSIONS,
  PORTAL_APPROVE_FALLBACK,
  PORTAL_MANAGE_CONTENT_FALLBACK,
  PORTAL_UPLOAD_FALLBACK,
  PORTAL_VIEW_FALLBACK,
} from './config/permissions.config';
import { AuthGuard } from './guards/AuthGuard';
import { GuestGuard } from './guards/GuestGuard';
import { PermissionGuard } from './guards/PermissionGuard';
import { SuperAdminGuard } from './guards/SuperAdminGuard';
import LoginPage from './pages/auth/LoginPage';
import AppShell from './components/layout/AppShell';
import DashboardPage from './pages/dashboard/DashboardPage';
import CaseListPage from './pages/cases/CaseListPage';
import CaseDetailPage from './pages/cases/CaseDetailPage';
import UploadPage from './pages/upload/UploadPage';
import UploadStatusPage from './pages/upload-status/UploadStatusPage';
import DraftsPage from './pages/drafts/DraftsPage';
import AccessRequestPage from './pages/access-request/AccessRequestPage';
import AccessStatusPage from './pages/access-status/AccessStatusPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import ContentManagementPage from './pages/content-management/ContentManagementPage';
import ArchivePage from './pages/archive/ArchivePage';
import SearchDocumentsPage from './pages/search/SearchDocumentsPage';
import FavoritesPage from './pages/favorites/FavoritesPage';
import LatestFilesPage from './pages/latest-files/LatestFilesPage';
import AuditPage from './pages/audit/AuditPage';
import ReportsPage from './pages/reports/ReportsPage';
import DocumentViewerPage from './pages/viewer/DocumentViewerPage';
import StorageSyncPage from './pages/storage-sync/StorageSyncPage';
import CaseQREntryPage from './pages/cases/CaseQREntryPage';
import CaseQRViewGate from './pages/cases/CaseQRViewGate';
import AdminApp from './pages/admin/AdminApp';
import { usePortalAccess } from './hooks/usePortalAccess';

const CaseDetailRoute: React.FC = () => {
  const location = useLocation();
  return <CaseDetailPage key={location.pathname} />;
};

const DefaultPortalRedirect: React.FC = () => {
  const { firstPath } = usePortalAccess();
  return <Navigate to={firstPath} replace />;
};

const App: React.FC = () => {
  return (
    <>
      <Routes>
          {/* Standalone Dynamic Admin Module — its own identity, auth & layout */}
          <Route path="/admin/*" element={<AdminApp />} />

          {/* QR scan entry — public; clears session and opens login every time */}
          <Route path={ROUTES.CASE_QR_ENTRY} element={<CaseQREntryPage />} />

          <Route element={<GuestGuard />}>
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
          </Route>

          <Route element={<AuthGuard />}>
            <Route
              element={
                <PermissionGuard
                  permission={PORTAL_PERMISSIONS.view}
                  fallbackRoles={PORTAL_VIEW_FALLBACK}
                />
              }
            >
              <Route path={ROUTES.DOCUMENT_VIEWER} element={<DocumentViewerPage />} />
              <Route path="/viewer/:documentId" element={<DocumentViewerPage />} />
            </Route>
            {/* Post-login case view (mobile-friendly, no AppShell) */}
            <Route path={ROUTES.CASE_QR_VIEW} element={<CaseQRViewGate />} />
            <Route element={<AppShell />}>
              <Route
                element={
                  <PermissionGuard
                    permission={PORTAL_PERMISSIONS.view}
                    fallbackRoles={PORTAL_VIEW_FALLBACK}
                  />
                }
              >
                <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
                <Route path={ROUTES.CASES} element={<CaseListPage />} />
                <Route path={ROUTES.CASE_DETAIL} element={<CaseDetailRoute />} />
                <Route path={ROUTES.ARCHIVE} element={<ArchivePage />} />
                <Route path={ROUTES.SEARCH} element={<SearchDocumentsPage />} />
                <Route path={ROUTES.FAVORITES} element={<FavoritesPage />} />
                <Route path={ROUTES.LATEST_FILES} element={<LatestFilesPage />} />
                <Route path={ROUTES.AUDIT} element={<AuditPage />} />
              </Route>

              <Route
                element={
                  <PermissionGuard
                    permission={PORTAL_PERMISSIONS.upload}
                    fallbackRoles={PORTAL_UPLOAD_FALLBACK}
                  />
                }
              >
                <Route path={ROUTES.UPLOAD} element={<UploadPage />} />
                <Route path={ROUTES.DRAFTS} element={<DraftsPage />} />
              </Route>

              <Route
                element={
                  <PermissionGuard
                    permission={PORTAL_PERMISSIONS.approve}
                    fallbackRoles={PORTAL_APPROVE_FALLBACK}
                  />
                }
              >
                <Route path={ROUTES.UPLOAD_STATUS} element={<UploadStatusPage />} />
              </Route>

              <Route
                element={
                  <PermissionGuard
                    permission={PORTAL_PERMISSIONS.approve}
                    fallbackRoles={PORTAL_APPROVE_FALLBACK}
                    absent
                  />
                }
              >
                <Route path={ROUTES.ACCESS_REQUEST} element={<AccessRequestPage />} />
                <Route path={ROUTES.ACCESS_STATUS} element={<AccessStatusPage />} />
              </Route>

              <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />

              <Route
                element={
                  <PermissionGuard
                    permission={PORTAL_PERMISSIONS.manageContent}
                    fallbackRoles={PORTAL_MANAGE_CONTENT_FALLBACK}
                  />
                }
              >
                <Route path={ROUTES.CONTENT_MGMT} element={<ContentManagementPage />} />
              </Route>

              <Route element={<PermissionGuard permission={PORTAL_PERMISSIONS.reports} />}>
                <Route path={ROUTES.REPORTS} element={<ReportsPage />} />
              </Route>

              <Route element={<SuperAdminGuard />}>
                <Route path={ROUTES.STORAGE_SYNC} element={<StorageSyncPage />} />
              </Route>
            </Route>
          </Route>

          <Route path="/" element={<DefaultPortalRedirect />} />
          <Route path="*" element={<DefaultPortalRedirect />} />
      </Routes>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          success: {
            style: {
              background: 'var(--color-success-light)',
              color: 'var(--color-success)',
              border: '1px solid var(--color-success)',
            },
          },
          error: {
            style: {
              background: 'var(--color-error-light)',
              color: 'var(--color-error)',
              border: '1px solid var(--color-error)',
            },
          },
        }}
      />
    </>
  );
};

export default App;
